import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAllContacts, setContactLinkedInConnected, setContactLinkedInUrl, getIndustryGroups } from '../services/api';
import AzotteGrid from '../components/AzotteGrid';
import LinkedInContactDialog from '../components/LinkedInContactDialog';
import ContactConversationDialog from '../components/ContactConversationDialog';
import ContactEditDialog from '../components/ContactEditDialog';
import ContactStatusDialog from '../components/ContactStatusDialog';
import BulkOutreachDialog from '../components/BulkOutreachDialog';
import {
  Box, Typography, TextField, Stack, Grid, Chip, Alert, MenuItem, CircularProgress,
  Select, FormControl, InputLabel, Button, Tooltip, Link, IconButton, Menu, Divider, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

const SENIORITY = ['Unknown', 'Executive', 'Director', 'Manager', 'Individual'];
const TRIBOOL = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const triParam = (v) => (v === 'true' ? true : v === 'false' ? false : undefined);

export default function ContactsPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [hasLinkedIn, setHasLinkedIn] = useState('true');
  const [linkedInConnected, setLinkedInConnected] = useState('');
  const [hasEmail, setHasEmail] = useState('');
  const [isPrimary, setIsPrimary] = useState('');
  const [isRecommended, setIsRecommended] = useState('');
  const [reachedOut, setReachedOut] = useState('');
  const [lastOutreachChannel, setLastOutreachChannel] = useState('');
  const [industryGroupId, setIndustryGroupId] = useState('');
  const [seniority, setSeniority] = useState('');

  const [industryGroups, setIndustryGroups] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [selectedContact, setSelectedContact] = useState(null);
  const [convoTarget, setConvoTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null); // { contact, action }
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [urlEdit, setUrlEdit] = useState(null); // { contact, value }
  const [savingUrl, setSavingUrl] = useState(false);

  const queryParams = useMemo(() => {
    const p = { page: page + 1, pageSize: rowsPerPage };
    if (search.trim()) p.search = search.trim();
    const hl = triParam(hasLinkedIn); if (hl !== undefined) p.hasLinkedIn = hl;
    const lc = triParam(linkedInConnected); if (lc !== undefined) p.linkedInConnected = lc;
    const he = triParam(hasEmail); if (he !== undefined) p.hasEmail = he;
    const ip = triParam(isPrimary); if (ip !== undefined) p.isPrimary = ip;
    const ir = triParam(isRecommended); if (ir !== undefined) p.isRecommended = ir;
    const ro = triParam(reachedOut); if (ro !== undefined) p.reachedOut = ro;
    if (lastOutreachChannel) p.lastOutreachChannel = lastOutreachChannel;
    if (industryGroupId) p.industryGroupId = industryGroupId;
    if (seniority) p.seniority = seniority;
    return p;
  }, [page, rowsPerPage, search, hasLinkedIn, linkedInConnected, hasEmail, isPrimary, isRecommended, reachedOut, lastOutreachChannel, industryGroupId, seniority]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listAllContacts(queryParams);
      setItems(data?.items || []);
      setTotalCount(data?.totalCount || 0);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    const t = setTimeout(fetchContacts, 250);
    return () => clearTimeout(t);
  }, [fetchContacts]);

  useEffect(() => { setPage(0); }, [search, hasLinkedIn, linkedInConnected, hasEmail, isPrimary, isRecommended, reachedOut, lastOutreachChannel, industryGroupId, seniority]);

  useEffect(() => {
    getIndustryGroups()
      .then(({ data }) => setIndustryGroups(data || []))
      .catch(() => {});
  }, []);

  const resetFilters = () => {
    setSearch('');
    setHasLinkedIn('');
    setLinkedInConnected('');
    setHasEmail('');
    setIsPrimary('');
    setIsRecommended('');
    setReachedOut('');
    setLastOutreachChannel('');
    setIndustryGroupId('');
    setSeniority('');
    setPage(0);
  };

  const fullName = (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || '(no name)';

  const toggleConnected = async (row) => {
    const next = !row.isLinkedInConnected;
    setTogglingId(row.id);
    setItems((prev) => prev.map((i) => i.id === row.id
      ? { ...i, isLinkedInConnected: next, linkedInConnectedAt: next ? new Date().toISOString() : null }
      : i));
    try {
      await setContactLinkedInConnected(row.id, next);
    } catch {
      setItems((prev) => prev.map((i) => i.id === row.id ? { ...i, isLinkedInConnected: !next } : i));
    } finally {
      setTogglingId(null);
    }
  };

  const saveUrl = async () => {
    if (!urlEdit) return;
    const { contact, value } = urlEdit;
    const url = (value || '').trim();
    setSavingUrl(true);
    try {
      await setContactLinkedInUrl(contact.id, url);
      setItems((prev) => prev.map((i) => i.id === contact.id ? { ...i, linkedInUrl: url || null } : i));
      setUrlEdit(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save LinkedIn URL');
    } finally {
      setSavingUrl(false);
    }
  };

  const deleteUrl = async (row) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Remove LinkedIn URL for ${fullName(row)}?`)) return;
    try {
      await setContactLinkedInUrl(row.id, '');
      setItems((prev) => prev.map((i) => i.id === row.id ? { ...i, linkedInUrl: null } : i));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to remove LinkedIn URL');
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const day = 86400000;
    if (diffMs < day) return 'today';
    if (diffMs < 2 * day) return 'yesterday';
    if (diffMs < 30 * day) return `${Math.floor(diffMs / day)}d ago`;
    if (diffMs < 365 * day) return `${Math.floor(diffMs / (30 * day))}mo ago`;
    return d.toLocaleDateString();
  };

  const seniorityColor = (s) => {
    if (s === 'Executive') return 'secondary';
    if (s === 'Director') return 'primary';
    if (s === 'Manager') return 'info';
    return 'default';
  };

  const columns = [
    {
      key: 'firstName',
      header: 'Name',
      render: (_v, row) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullName(row)}</Typography>
            {row.jobTitle && (
              <Typography variant="caption" color="text.secondary">
                {row.jobTitle}{row.department ? ` · ${row.department}` : ''}
              </Typography>
            )}
          </Box>
          {row.isPrimary && <Chip label="Primary" size="small" color="warning" variant="outlined" />}
          {row.isRecommendedForOutreach && <Chip label="Reco" size="small" color="success" variant="outlined" />}
          {row.outreachCount > 0 && (
            <Tooltip title={`${row.outreachCount} outbound · last ${formatRelative(row.lastOutreachAt) || ''}${row.lastOutreachChannel ? ` (${row.lastOutreachChannel})` : ''}`}>
              <Chip
                icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 14 }} />}
                label="Reached"
                size="small"
                color="info"
                variant="filled"
              />
            </Tooltip>
          )}
          {row.isUnsubscribed && <Chip label="Unsub" size="small" color="error" variant="outlined" />}
          {row.isInvalid && <Chip label="Invalid" size="small" color="error" />}
          {row.isEmailInvalid && <Chip label="Bounce" size="small" color="error" variant="outlined" />}
        </Stack>
      ),
    },
    {
      key: 'leadCompanyName',
      header: 'Company',
      render: (v, row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); navigate(`/leads/${row.leadId}`); }}
        >
          {v}
          {row.leadCountry && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {row.leadCountry}
            </Typography>
          )}
        </Typography>
      ),
    },
    {
      key: 'leadIndustryName',
      header: 'Industry',
      hideOnMobile: true,
      hideOnTablet: true,
      render: (v) => v || <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'seniorityLevel',
      header: 'Seniority',
      width: 110,
      hideOnMobile: true,
      render: (v) => v && v !== 'Unknown'
        ? <Chip label={v} size="small" color={seniorityColor(v)} variant="outlined" />
        : <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'email',
      header: 'Email',
      hideOnMobile: true,
      render: (v) => v
        ? <Link href={`mailto:${v}`} variant="body2" onClick={(e) => e.stopPropagation()}>{v}</Link>
        : <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'linkedInUrl',
      header: 'LinkedIn',
      width: 150,
      render: (v, row) => (
        <Stack direction="row" spacing={0.25} alignItems="center">
          {v ? (
            <>
              <LinkedInIcon fontSize="small" sx={{ color: '#0a66c2' }} />
              <Link href={v} target="_blank" rel="noopener" variant="caption" onClick={(e) => e.stopPropagation()}>
                Profile <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
              </Link>
              <Tooltip title="Edit LinkedIn URL">
                <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); setUrlEdit({ contact: row, value: v }); }}>
                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove LinkedIn URL">
                <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); deleteUrl(row); }}>
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} color="error" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Add LinkedIn URL">
              <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); setUrlEdit({ contact: row, value: '' }); }}>
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      key: 'isLinkedInConnected',
      header: 'Connected',
      width: 120,
      render: (v, row) => (
        <Tooltip title={v
          ? `LinkedIn connected${row.linkedInConnectedAt ? ` · ${formatRelative(row.linkedInConnectedAt)}` : ''} — click to clear`
          : 'Not connected — click to mark connected'}>
          <Chip
            icon={togglingId === row.id
              ? <CircularProgress size={12} />
              : (v ? <HowToRegOutlinedIcon sx={{ fontSize: 16 }} /> : <PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />)}
            label={v ? 'Connected' : 'Connect'}
            size="small"
            color={v ? 'success' : 'default'}
            variant={v ? 'filled' : 'outlined'}
            disabled={togglingId === row.id}
            onClick={(e) => { e.stopPropagation(); toggleConnected(row); }}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
      ),
    },
    {
      key: 'outreachScore',
      header: 'Score',
      width: 70,
      hideOnMobile: true,
      hideOnTablet: true,
      render: (v) => v ? <Chip label={v} size="small" variant="outlined" /> : <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'lastOutreachAt',
      header: 'Last reach',
      width: 130,
      hideOnMobile: true,
      render: (v, row) => v
        ? (
          <Box>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{formatRelative(v)}</Typography>
            {row.lastOutreachChannel && (
              <Typography variant="caption" color="text.secondary">{row.lastOutreachChannel}</Typography>
            )}
          </Box>
        )
        : <Typography color="text.disabled">-</Typography>,
    },
    {
      key: 'id',
      header: '',
      width: 180,
      render: (_v, row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={row.linkedInUrl ? 'Insights & LinkedIn helper' : 'Insights (no LinkedIn URL)'}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<LightbulbOutlinedIcon />}
                onClick={(e) => { e.stopPropagation(); setSelectedContact(row); }}
              >
                Assist
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Conversation & paste reply">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setConvoTarget(row); }}
            >
              <ForumOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuRow(row); }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const stats = [
    { label: 'Total', value: totalCount, color: 'primary.light', icon: <PeopleAltOutlinedIcon /> },
    { label: 'With LinkedIn', value: items.filter(i => i.linkedInUrl).length, color: 'info.light', icon: <LinkedInIcon /> },
    { label: 'Connected', value: items.filter(i => i.isLinkedInConnected).length, color: 'success.light', icon: <HowToRegOutlinedIcon /> },
    { label: 'Reached', value: items.filter(i => i.outreachCount > 0).length, color: 'info.light', icon: <MarkEmailReadOutlinedIcon /> },
    { label: 'Recommended', value: items.filter(i => i.isRecommendedForOutreach).length, color: 'warning.light', icon: <AutoAwesomeOutlinedIcon /> },
    {
      label: 'Suppressed',
      value: items.filter(i => i.isUnsubscribed || i.isInvalid || i.isEmailInvalid).length,
      color: 'error.light',
      icon: <BlockOutlinedIcon />,
    },
  ];

  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const openStatus = (action) => {
    if (!menuRow) return;
    setStatusTarget({ contact: menuRow, action });
    closeMenu();
  };
  const openEdit = () => { if (menuRow) { setEditTarget(menuRow); closeMenu(); } };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">Contacts</Typography>
          <Typography variant="body2" color="text.secondary">
            All lead contacts. Filter for LinkedIn reach-outs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<EmailOutlinedIcon />}
            onClick={() => setBulkOpen(true)}
          >
            Email filtered
          </Button>
          <Button startIcon={<RestartAltIcon />} onClick={resetFilters}>Reset filters</Button>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((s) => (
          <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
            <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: s.color }}>{s.icon}</Box>
              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Stack spacing={2}>
          <TextField
            placeholder="Search name, email, title, LinkedIn URL, or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
          />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Has LinkedIn</InputLabel>
                <Select value={hasLinkedIn} label="Has LinkedIn" onChange={(e) => setHasLinkedIn(e.target.value)}>
                  {TRIBOOL.map((t) => <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>LinkedIn connected</InputLabel>
                <Select value={linkedInConnected} label="LinkedIn connected" onChange={(e) => setLinkedInConnected(e.target.value)}>
                  {TRIBOOL.map((t) => <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Has Email</InputLabel>
                <Select value={hasEmail} label="Has Email" onChange={(e) => setHasEmail(e.target.value)}>
                  {TRIBOOL.map((t) => <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Primary</InputLabel>
                <Select value={isPrimary} label="Primary" onChange={(e) => setIsPrimary(e.target.value)}>
                  {TRIBOOL.map((t) => <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Recommended</InputLabel>
                <Select value={isRecommended} label="Recommended" onChange={(e) => setIsRecommended(e.target.value)}>
                  {TRIBOOL.map((t) => <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Seniority</InputLabel>
                <Select value={seniority} label="Seniority" onChange={(e) => setSeniority(e.target.value)}>
                  <MenuItem value="">Any</MenuItem>
                  {SENIORITY.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Reached out</InputLabel>
                <Select value={reachedOut} label="Reached out" onChange={(e) => setReachedOut(e.target.value)}>
                  {TRIBOOL.map((t) => <MenuItem key={t.value || 'any'} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Last reach-out</InputLabel>
                <Select value={lastOutreachChannel} label="Last reach-out" onChange={(e) => setLastOutreachChannel(e.target.value)}>
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="LinkedInManual">LinkedIn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Industry group</InputLabel>
                <Select value={industryGroupId} label="Industry group" onChange={(e) => setIndustryGroupId(e.target.value)}>
                  <MenuItem value="">Any</MenuItem>
                  {industryGroups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <AzotteGrid
          columns={columns}
          rows={items}
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          loading={loading}
          onDoubleClick={(row) => setSelectedContact(row)}
        />
      )}

      <BulkOutreachDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        filters={queryParams}
        onQueued={fetchContacts}
      />

      <LinkedInContactDialog
        open={!!selectedContact}
        contactId={selectedContact?.id}
        onClose={() => setSelectedContact(null)}
      />

      <ContactConversationDialog
        open={!!convoTarget}
        leadId={convoTarget?.leadId}
        contact={convoTarget}
        onClose={() => setConvoTarget(null)}
        onLogged={fetchContacts}
      />

      <ContactEditDialog
        open={!!editTarget}
        leadId={editTarget?.leadId}
        contact={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={fetchContacts}
      />

      <ContactStatusDialog
        open={!!statusTarget}
        leadId={statusTarget?.contact?.leadId}
        contact={statusTarget?.contact}
        action={statusTarget?.action}
        onClose={() => setStatusTarget(null)}
        onChanged={fetchContacts}
      />

      <Dialog open={!!urlEdit} onClose={savingUrl ? undefined : () => setUrlEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{urlEdit?.contact?.linkedInUrl ? 'Edit' : 'Add'} LinkedIn URL</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {urlEdit ? fullName(urlEdit.contact) : ''}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="LinkedIn URL"
            placeholder="https://www.linkedin.com/in/..."
            value={urlEdit?.value || ''}
            onChange={(e) => setUrlEdit((p) => ({ ...p, value: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') saveUrl(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUrlEdit(null)} disabled={savingUrl}>Cancel</Button>
          <Button variant="contained" onClick={saveUrl} disabled={savingUrl}>
            {savingUrl ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={openEdit}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit contact info</ListItemText>
        </MenuItem>
        <Divider />
        {menuRow?.isUnsubscribed ? (
          <MenuItem onClick={() => openStatus('resubscribe')}>
            <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Resubscribe</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => openStatus('unsubscribe')}>
            <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Unsubscribe</ListItemText>
          </MenuItem>
        )}
        {menuRow?.isInvalid ? (
          <MenuItem onClick={() => openStatus('revalidate')}>
            <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Clear invalid flag</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => openStatus('invalid')}>
            <ListItemIcon><ReportGmailerrorredIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Mark invalid</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => openStatus('delete')} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete contact</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
